import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { PublicRoute } from '@/components/auth/PublicRoute';
import { LoaderIcon } from 'lucide-react';
import { ROUTES } from './routeConfig';
import BuilderLayout from '@/pages/builder/BuilderLayout';

// Wrapper to force remount when interfaceId changes
const BuilderEditorPageWrapper = () => {
  const { interfaceId } = useParams<{ interfaceId?: string }>();
  return <BuilderEditorPage key={interfaceId} />;
};

const BuilderPublishPageWrapper = () => {
  const { interfaceId } = useParams<{ interfaceId?: string }>();
  return <BuilderPublishPage key={interfaceId} />;
};

// Lazy load pages for code splitting
const EntryPage = lazy(() => import('@/pages/EntryPage'));
const Login = lazy(() => import('@/pages/login'));
const Signup = lazy(() => import('@/pages/signup'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const BuilderEditorPage = lazy(() => import('@/pages/builder/BuilderEditor'));
const BuilderTemplatePreviewPage = lazy(
  () => import('@/pages/builder/TemplatePreview')
);
const BuilderSettingsPage = lazy(() => import('@/pages/settings'));
const BuilderPublishPage = lazy(
  () => import('@/pages/builder/PublishPage')
);
const ModuleInterfaces = lazy(() => import('@/pages/builder/ModuleInterfaces'));
const Dashboard = lazy(() => import('@/pages/dashboard'));
const CreateNewWebsite = lazy(() => import('@/pages/create-new-website'));
const BuilderJsonTestPage = lazy(() => import('@/pages/builder/BuilderJsonTest'));

// Component to handle redirect from base /builder/{moduleId}/{interfaceId} route
const BuilderIndexRedirect = () => {
  const { landingPageID, interfaceId } = useParams<{
    landingPageID: string;
    interfaceId: string;
  }>();

  if (!landingPageID || !interfaceId) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return (
    <Navigate
      to={`${ROUTES.BUILDER}/${landingPageID}/${interfaceId}/${ROUTES.BUILDER_TEMPLATE_PREVIEW}`}
      replace
    />
  );
};

/**
 * Application Routes Configuration
 *
 * Add new routes here following the pattern:
 * - Public routes: Wrap with <PublicRoute>
 * - Protected routes: Wrap with <ProtectedRoute>
 * - Dynamic routes: Use :paramName syntax
 */
export const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route
          path={ROUTES.LOGIN}
          element={
            <PublicRoute>
              <Suspense fallback={<PageLoader />}>
                <Login />
              </Suspense>
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.SIGNUP}
          element={
            <PublicRoute>
              <Suspense fallback={<PageLoader />}>
                <Signup />
              </Suspense>
            </PublicRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path={ROUTES.ENTRY}
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <EntryPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DASHBOARD}
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <Dashboard />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CREATE_NEW_WEBSITE}
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <CreateNewWebsite />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.BUILDER_TEST}
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <BuilderJsonTestPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* Route for /builder/{moduleId} - base route with BuilderLayout */}
        <Route
          path={`${ROUTES.BUILDER}/:landingPageID`}
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <BuilderLayout />
              </Suspense>
            </ProtectedRoute>
          }
        >
          {/* Route for /builder/{moduleId}/editor - editor without interfaceId */}
          <Route path={ROUTES.BUILDER_EDITOR} element={<BuilderEditorPage />} />
          {/* Route for /builder/{moduleId} - fetches interfaces and redirects (index route) */}
          <Route index element={<ModuleInterfaces />} />
        </Route>
        {/* Route for /builder/{moduleId}/{interfaceId} - shows template preview or editor */}
        <Route
          path={`${ROUTES.BUILDER}/:landingPageID/:interfaceId`}
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <BuilderLayout />
              </Suspense>
            </ProtectedRoute>
          }
        >
          {/* Index route - redirects to template-preview when visiting base route */}
          <Route index element={<BuilderIndexRedirect />} />
          <Route
            path={ROUTES.BUILDER_TEMPLATE_PREVIEW}
            element={<BuilderTemplatePreviewPage />}
          />
          <Route path={ROUTES.BUILDER_EDITOR} element={<BuilderEditorPageWrapper />} />
          <Route
            path={ROUTES.BUILDER_SETTINGS}
            element={<BuilderSettingsPage />}
          />
          <Route
            path={ROUTES.BUILDER_PUBLISH}
            element={<BuilderPublishPageWrapper />}
          />
        </Route>

        {/* Catch-all route - must be last */}
        <Route
          path='*'
          element={
            <Suspense fallback={<PageLoader />}>
              <NotFound />
            </Suspense>
          }
        />
      </Routes>
    </Suspense>
  );
};

// Loading fallback component
const PageLoader = () => (
  <div className='min-h-screen w-full bg-gradient-to-br from-gray-50 to-indigo-50 flex items-center justify-center'>
    <div className='flex flex-col items-center space-y-4'>
      <LoaderIcon className='animate-spin h-8 w-8' />
      <p className='text-gray-600'>Loading...</p>
    </div>
  </div>
);
